import { Button, Form, Input, Modal, Switch } from 'antd';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { BRAND } from '@/constants/brand';
import { useNotify } from '@/hooks/useNotify';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useMutationReset } from '@/hooks/useMutationReset';
import {
  useCreateAttributeMutation,
  useUpdateAttributeMutation,
} from '@/modules/attributes/attributesApi';
import type { Attribute } from '@/types/attribute';
import { resolveMutationResult } from '@/utils/apiResponse';

interface AttributeFormModalProps {
  open: boolean;
  attribute?: Attribute | null;
  onClose: () => void;
}

interface AttributeFormValues {
  name: string;
  isActive: boolean;
}

export function AttributeFormModal({ open, attribute, onClose }: AttributeFormModalProps) {
  const notify = useNotify();
  const isEdit = Boolean(attribute);
  const [createAttribute, createState] = useCreateAttributeMutation();
  const [updateAttribute, updateState] = useUpdateAttributeMutation();
  const isLoading = createState.isLoading || updateState.isLoading;
  useGlobalLoader(isLoading);

  const { control, handleSubmit, reset } = useForm<AttributeFormValues>({
    defaultValues: {
      name: '',
      isActive: true,
    },
  });

  useMutationReset(createState.reset);
  useMutationReset(updateState.reset);

  useEffect(() => {
    if (!open) return;

    reset({
      name: attribute?.name ?? '',
      isActive: attribute?.isActive ?? true,
    });
  }, [attribute, open, reset]);

  const onSubmit = async (values: AttributeFormValues) => {
    const payload = {
      name: values.name.trim(),
      isActive: values.isActive,
    };

    if (isEdit) {
      if (!attribute?.id) return;

      const result = await resolveMutationResult(updateAttribute({ id: attribute.id, body: payload }));
      if (result.ok) {
        notify.success(result.message ?? `Attribute ${isEdit ? 'updated' : 'created'} successfully.`);
        onClose();
        return;
      }

      notify.error(result.message);
      return;
    }

    const result = await resolveMutationResult(createAttribute(payload));

    if (result.ok) {
      notify.success(result.message ?? `Attribute ${isEdit ? 'updated' : 'created'} successfully.`);
      onClose();
      return;
    }

    notify.error(result.message);
  };

  return (
    <Modal
      title={isEdit ? 'Edit attribute' : 'Add attribute'}
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnHidden
      width={480}
    >
      <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
        <Controller
          name="name"
          control={control}
          rules={{ required: 'Name is required' }}
          render={({ field, fieldState }) => (
            <Form.Item
              label="Name"
              required
              validateStatus={fieldState.error ? 'error' : ''}
              help={fieldState.error?.message}
            >
              <Input {...field} placeholder="e.g. Format" />
            </Form.Item>
          )}
        />

        <Controller
          name="isActive"
          control={control}
          render={({ field }) => (
            <Form.Item label="Status">
              <Switch
                checked={field.value}
                onChange={field.onChange}
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                style={{ background: field.value ? BRAND.primary : undefined }}
              />
            </Form.Item>
          )}
        />

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            {isEdit ? 'Save changes' : 'Create attribute'}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
